import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type TicketDetail, getTicket, replyToTicket } from '~/api/support';
import { useSession } from '~/lib/auth';
import { buildHead } from '~/seo/site';

export const Route = createFileRoute('/support/$id')({
  head: () =>
    buildHead({
      title: 'Ticket',
      description: 'Support ticket detail.',
      path: '/support',
      noindex: true,
    }),
  component: TicketDetailPage,
});

function TicketDetailPage() {
  const { id } = Route.useParams();
  const { data: session, isLoading: sessionLoading } = useSession();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const t = await getTicket(id);
      setTicket(t);
    } catch (err) {
      setError((err as Error).message ?? 'Could not load ticket.');
    } finally {
      setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load/navigate/session are stable; id + sessionLoading are the real triggers
  useEffect(() => {
    if (!sessionLoading && !session) {
      navigate({ to: '/auth/sign-in' });
      return;
    }
    if (!sessionLoading) {
      load();
    }
  }, [id, sessionLoading]);

  if (sessionLoading || loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12">
        <p className="text-destructive">{error}</p>
      </section>
    );
  }

  if (!ticket) return null;

  const description = ticket.replies[0];
  const followUps = ticket.replies.slice(1);

  return (
    <section className="mx-auto max-w-2xl space-y-6 px-4 py-12">
      <button
        type="button"
        onClick={() => navigate({ to: '/support' })}
        className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to tickets
      </button>

      <header className="space-y-1">
        <div className="flex items-start gap-3">
          <h1 className="flex-1 text-xl font-bold">{ticket.subject}</h1>
          <StatusBadge status={ticket.status} />
        </div>
        <p className="font-mono text-[11px] text-muted-foreground">
          Opened {new Date(ticket.createdAt).toLocaleDateString()}
        </p>
      </header>

      {/* First reply is the original message the user wrote when opening the
          ticket; render it under a distinct "Description" header so it
          doesn't look like just another conversation entry. */}
      {description && (
        <div className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            Description
          </p>
          <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="font-mono text-xs font-bold">
                @{description.authorHandle ?? 'unknown'}
              </span>
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                {new Date(description.createdAt).toLocaleString()}
              </span>
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">{description.body}</p>
          </div>
        </div>
      )}

      {followUps.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
            Conversation
          </p>
          <div className="space-y-3">
            {followUps.map((r) => (
              <div
                key={r.id}
                className={`rounded-lg border px-4 py-3 text-sm ${
                  r.authorRole === 'admin'
                    ? 'border-primary/30 bg-primary/5'
                    : 'border-border bg-card'
                }`}
              >
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="font-mono text-xs font-bold">
                    @{r.authorHandle ?? 'unknown'}
                  </span>
                  {r.authorRole === 'admin' && (
                    <span className="rounded-full bg-primary/20 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-primary">
                      support
                    </span>
                  )}
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                    {new Date(r.createdAt).toLocaleString()}
                  </span>
                </div>
                <p className="whitespace-pre-wrap leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {ticket.status !== 'closed' ? (
        <ReplyBox ticketId={ticket.id} onReplied={() => navigate({ to: '/support' })} />
      ) : (
        <p className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-center font-mono text-sm text-muted-foreground">
          This ticket is closed.
        </p>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: TicketDetail['status'] }) {
  const cls = {
    open: 'bg-emerald-400/15 text-emerald-400',
    answered: 'bg-primary/15 text-primary',
    closed: 'bg-muted/40 text-muted-foreground',
  }[status];
  return (
    <span
      className={`shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`}
    >
      {status}
    </span>
  );
}

function ReplyBox({
  ticketId,
  onReplied,
}: {
  ticketId: string;
  onReplied: () => void;
}) {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setError(null);
    setSaving(true);
    try {
      await replyToTicket(ticketId, body);
      setBody('');
      onReplied();
    } catch (err) {
      setError((err as Error).message ?? 'Could not send reply.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-border bg-card p-4">
      <label
        htmlFor="reply-body"
        className="font-mono text-xs uppercase tracking-wider text-muted-foreground"
      >
        Add reply
      </label>
      <textarea
        id="reply-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
        minLength={1}
        maxLength={4000}
        rows={4}
        placeholder="Type your reply..."
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <button
        type="submit"
        disabled={saving || !body.trim()}
        className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 font-mono text-sm font-bold uppercase tracking-wider text-primary-foreground disabled:opacity-60"
      >
        {saving && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
        Send reply
      </button>
    </form>
  );
}
